import React, { useState } from 'react';
import Layout from '../../components/Layout';
import { apiFetch } from '../../lib/api';
import { useFiltroSocieta } from '../../hooks/useFiltroSocieta';

const TIPOLOGIE_ARTICOLO: { value: string; label: string; recommended: boolean }[] = [
  { value: 'ARTICOLO_FISICO',            label: 'Articolo fisico',              recommended: true  },
  { value: 'PRODOTTO_CONFIGURATO',       label: 'Prodotto configurato',         recommended: true  },
  { value: 'INTERCAMBIABILE',            label: 'Intercambiabile',              recommended: false },
  { value: 'RAGGRUPPAMENTO_PREZZO_CORPO',label: 'Raggruppamento prezzo corpo',  recommended: false },
  { value: 'LAVORAZIONE_PRESTAZIONE',    label: 'Lavorazione / prestazione',    recommended: false },
  { value: 'CONSUMI',                    label: 'Consumi',                      recommended: false },
];

// SVG Icons
const PlayIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="5 3 19 12 5 21 5 3"></polygon>
  </svg>
);

const CheckIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"></polyline>
  </svg>
);

export default function InitAfterReleasePage() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState('');

  // ── Setup magazzino: tipologie da attivare ───────────────────────────────
  const { societaId, setSocietaId, listaSocieta, isUnica } = useFiltroSocieta();
  const [tipologieScelte, setTipologieScelte] = useState<Set<string>>(
    new Set(TIPOLOGIE_ARTICOLO.filter(t => t.recommended).map(t => t.value))
  );
  const [magLoading, setMagLoading] = useState(false);
  const [magResult, setMagResult] = useState<{ aggiornati: number; valore: boolean } | null>(null);
  const [magError, setMagError] = useState('');

  const toggleTipologia = (v: string) => {
    setTipologieScelte(s => {
      const next = new Set(s);
      if (next.has(v)) next.delete(v); else next.add(v);
      return next;
    });
  };

  const eseguiBulkMagazzino = async (valore: boolean) => {
    if (!societaId) { setMagError('Seleziona prima una società'); return; }
    if (tipologieScelte.size === 0) { setMagError('Seleziona almeno una tipologia'); return; }
    const tipologie = Array.from(tipologieScelte);
    const azione = valore ? 'ATTIVARE' : 'DISATTIVARE';
    if (!window.confirm(`${azione} la gestione magazzino su TUTTI gli articoli con tipologia ${tipologie.join(', ')} nella società selezionata?\n\nL'operazione aggiorna in massa e poi puoi sempre modificare singoli articoli.`)) {
      return;
    }
    setMagLoading(true); setMagError(''); setMagResult(null);
    try {
      // @api POST /api/anagrafiche/articoli/bulk-gestione-magazzino { societaId, tipologie, valore } -> { success: true, data: { aggiornati, valore } }
      const res = await apiFetch<{ success: boolean; data: { aggiornati: number; valore: boolean } }>(
        '/api/anagrafiche/articoli/bulk-gestione-magazzino',
        { method: 'POST', body: JSON.stringify({ societaId: Number(societaId), tipologie, valore }) },
      );
      setMagResult(res.data);
    } catch (e: any) {
      setMagError(e.message || 'Errore nell\'operazione massiva');
    } finally {
      setMagLoading(false);
    }
  };

  const handleInitPermessi = async () => {
    if (!window.confirm('Vuoi inizializzare tutti i permessi per tutti gli utenti?\n\nQuesta operazione:\n- Aggiunge i permessi mancanti per i nuovi moduli\n- NON sovrascrive i permessi esistenti\n- È sicura e idempotente')) {
      return;
    }

    try {
      setLoading(true);
      setError('');
      setResult(null);

      const data = await apiFetch('/api/permessi/inizializza-tutti', {
        method: 'POST',
      });

      setResult(data);
    } catch (err: any) {
      setError(err.message || 'Errore durante l\'inizializzazione dei permessi');
    } finally {
      setLoading(false);
    }
  };

  // ── Knowledge base ───────────────────────────────────────────────────────
  const [kbLoading, setKbLoading] = useState(false);
  const [kbResult, setKbResult] = useState<{ creati: number; aggiornati: number; disattivati: number; invariati: number; scartati: { file: string; errore: string }[] } | null>(null);
  const [kbError, setKbError] = useState('');

  const handleSyncKb = async () => {
    setKbLoading(true); setKbError(''); setKbResult(null);
    try {
      // @api POST /api/assistenza/kb/sync -> { success: true, data: { creati, aggiornati, disattivati, invariati, scartati: [{ file, errore }] } }
      const res = await apiFetch<{ success: boolean; data: NonNullable<typeof kbResult> }>('/api/assistenza/kb/sync', { method: 'POST' });
      setKbResult(res.data);
    } catch (e: any) {
      setKbError(e.message || 'Errore nella sincronizzazione');
    } finally {
      setKbLoading(false);
    }
  };

  return (
    <Layout>
      <div className="bop-page-header">
        <div className="bop-page-header__text">
          <h2>Inizializza dopo rilascio</h2>
          <p>Operazioni da eseguire dopo ogni deploy in produzione</p>
        </div>
      </div>

      <div className="bop-card">
        <h3 className="bop-init-release__title">
          Inizializzazione Permessi
        </h3>

        <div className="bop-init-release__info-box">
          <h4 className="bop-init-release__info-heading">
            ℹ️ Quando eseguire questa operazione:
          </h4>
          <ul className="bop-init-release__info-list">
            <li>Dopo ogni deploy che aggiunge nuovi moduli di permessi</li>
            <li>Quando si aggiungono nuove voci di menu con relativo permesso granulare</li>
            <li>Se nuovi utenti non vedono i moduli appena rilasciati</li>
          </ul>
        </div>

        <div className="bop-init-release__info-box">
          <h4 className="bop-init-release__info-heading">
            🧭 Struttura permessi (1:1 col menu)
          </h4>
          <ul className="bop-init-release__info-list">
            <li>Ogni voce di menu ha il suo permesso dedicato (granularità completa)</li>
            <li>I permessi sono raggruppati per <strong>macro → gruppo → voce</strong>, esattamente come la sidebar</li>
            <li>Source of truth: <code>backend/src/config/moduliPermessi.js</code> (campi <code>macro</code>, <code>gruppo</code>, <code>voce</code>)</li>
            <li>Per aggiungere un nuovo modulo: vedere CLAUDE.md (sezione "Nuova voce di menu")</li>
          </ul>
        </div>

        <div className="bop-init-release__warning-box">
          <h4 className="bop-init-release__warning-heading">
            ⚠️ Cosa fa questa operazione:
          </h4>
          <ul className="bop-init-release__warning-list">
            <li>Per ogni utente, per ogni società, crea i record di permesso mancanti per i nuovi moduli</li>
            <li>I permessi vengono creati con valore di default: <code>canView=false, canEdit=false, canDelete=false, canExport=false</code></li>
            <li>I permessi già esistenti <strong>NON vengono modificati</strong></li>
            <li>L'operazione è <strong>sicura e idempotente</strong> (può essere eseguita più volte senza problemi)</li>
          </ul>
        </div>

        <div className="bop-init-release__action-area">
          <button
            className="bop-btn-primary bop-init-release__run-btn"
            onClick={handleInitPermessi}
            disabled={loading}
          >
            <PlayIcon />
            {loading ? 'Inizializzazione in corso...' : 'Inizializza Permessi'}
          </button>
        </div>

        {error && (
          <div className="bop-init-release__error-box">
            <strong>Errore:</strong> {error}
          </div>
        )}

        {result && (
          <div className="bop-init-release__success-box">
            <div className="bop-init-release__success-header">
              <div className="bop-init-release__success-icon">
                <CheckIcon />
              </div>
              <h4 className="bop-init-release__success-title">
                Inizializzazione completata con successo!
              </h4>
            </div>

            <div className="bop-init-release__success-body">
              {result.message && <p className="bop-init-release__result-line">{result.message}</p>}

              {result.utentiProcessati !== undefined && (
                <p className="bop-init-release__result-line">
                  <strong>Assegnazioni utente×società processate:</strong> {result.utentiProcessati}
                </p>
              )}

              {result.permessiCreati !== undefined && (
                <p className="bop-init-release__result-line">
                  <strong>Permessi creati:</strong> {result.permessiCreati}
                </p>
              )}

              {result.permessiEsistenti !== undefined && (
                <p className="bop-init-release__result-line">
                  <strong>Permessi già esistenti:</strong> {result.permessiEsistenti}
                </p>
              )}

              <div className="bop-init-release__next-step">
                <strong>Prossimo passo:</strong> Gli amministratori possono ora andare nella pagina{' '}
                <strong>Permessi</strong> e assegnare i permessi necessari agli utenti per i nuovi moduli.
              </div>
            </div>
          </div>
        )}

        <div className="bop-init-release__tech-note">
          <h4 className="bop-init-release__tech-note-title">
            📖 Nota tecnica
          </h4>
          <p className="bop-init-release__tech-note-text">
            Questa funzione chiama l'endpoint <code>POST /api/permessi/inizializza-tutti</code>.
            <br />
            Per maggiori dettagli, consultare il file{' '}
            <code>backend/src/routes/permessi.js</code>.
          </p>
        </div>
      </div>

      {/* ── Knowledge base assistente ───────────────────────────────────── */}
      <div className="bop-card">
        <h3 className="bop-init-release__title">Knowledge base dell'assistente</h3>
        <p className="bop-text-muted">
          I documenti di <code>backend/kb</code> vengono caricati in tabella a ogni avvio del backend.
          Questo pulsante forza il caricamento senza riavviare: utile dopo un rilascio, o per vedere quali file sono stati scartati.
        </p>
        <div className="bop-init-release__action-area">
          <button className="bop-btn-primary bop-init-release__run-btn" onClick={handleSyncKb} disabled={kbLoading}>
            <PlayIcon />
            {kbLoading ? 'Sincronizzazione in corso...' : 'Aggiorna knowledge base'}
          </button>
        </div>
        {kbError && <div className="bop-init-release__error-box"><strong>Errore:</strong> {kbError}</div>}
        {kbResult && (
          <div className="bop-init-release__success-box">
            <div className="bop-init-release__success-body">
              <p className="bop-init-release__result-line"><strong>Creati:</strong> {kbResult.creati} · <strong>Aggiornati:</strong> {kbResult.aggiornati} · <strong>Disattivati:</strong> {kbResult.disattivati} · <strong>Invariati:</strong> {kbResult.invariati}</p>
              {kbResult.scartati.length > 0 && (
                <ul className="bop-init-release__warning-list">
                  {kbResult.scartati.map(s => <li key={s.file}><code>{s.file}</code>: {s.errore}</li>)}
                </ul>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── Setup Magazzino ──────────────────────────────────────────────── */}
      <div className="bop-card">
        <h3 className="bop-init-release__title">Quali articoli vuoi gestire a magazzino?</h3>

        <p className="bop-text-muted">
          Il magazzino tiene il conto di articoli che si toccano e si contano: prodotti finiti, materiali, ricambi.
          Non ha senso invece per licenze, canoni o servizi. Qui scegli in blocco — per tipologia — quali articoli di una società
          entrano in magazzino; potrai sempre modificare il singolo articolo dalla sua scheda.
        </p>

        {!isUnica && (
          <div className="bop-form-row">
            <div className="bop-form-group">
              <label className="bop-label">Società su cui operare</label>
              <select className="bop-select" value={societaId} onChange={e => setSocietaId(e.target.value)}>
                <option value="">Seleziona società…</option>
                {listaSocieta.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
              </select>
            </div>
          </div>
        )}

        <div className="bop-form-group">
          <label className="bop-label">Tipologie articolo da includere in magazzino</label>
          {TIPOLOGIE_ARTICOLO.map(t => (
            <div key={t.value} className="articolo-checkbox-group">
              <input
                type="checkbox"
                id={`tip-${t.value}`}
                checked={tipologieScelte.has(t.value)}
                onChange={() => toggleTipologia(t.value)}
              />
              <label htmlFor={`tip-${t.value}`}>
                {t.label}
                {t.recommended && <span className="bop-text-muted">&nbsp;— scelta consigliata</span>}
              </label>
            </div>
          ))}
        </div>

        <div className="bop-init-release__action-area bop-flex">
          <button
            className="bop-btn-primary bop-init-release__run-btn"
            onClick={() => eseguiBulkMagazzino(true)}
            disabled={magLoading || !societaId || tipologieScelte.size === 0}
          >
            <PlayIcon />
            {magLoading ? 'Aggiornamento…' : 'Includi in magazzino gli articoli di queste tipologie'}
          </button>
          <button
            className="bop-btn-secondary"
            onClick={() => eseguiBulkMagazzino(false)}
            disabled={magLoading || !societaId || tipologieScelte.size === 0}
          >
            Escludi dal magazzino
          </button>
        </div>

        <p className="bop-text-muted">
          Operazione ripetibile in sicurezza: ri-eseguirla non duplica nulla. Gli articoli inclusi
          compaiono nell'autocomplete quando crei un movimento; quelli esclusi non sono movimentabili a magazzino.
        </p>

        {magError && (
          <div className="bop-init-release__error-box"><strong>Errore:</strong> {magError}</div>
        )}

        {magResult && (
          <div className="bop-init-release__success-box">
            <div className="bop-init-release__success-header">
              <div className="bop-init-release__success-icon"><CheckIcon /></div>
              <h4 className="bop-init-release__success-title">
                {magResult.valore
                  ? `${magResult.aggiornati} articoli ora sono gestiti a magazzino.`
                  : `${magResult.aggiornati} articoli non sono più gestiti a magazzino.`}
              </h4>
            </div>
            <div className="bop-init-release__success-body">
              <div className="bop-init-release__next-step">
                <strong>Prossimo passo:</strong> vai in <strong>Magazzino / Logistica → Movimenti</strong> e
                crea un movimento; nell'elenco articoli vedrai ora quelli che hai appena incluso.
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
